import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, Cpu, CheckCircle2, XCircle, Clock,
  ChevronLeft, ChevronRight, ExternalLink, Search,
  ArrowUp, ArrowDown, ArrowUpDown, FilterX, GripVertical, Trash2,
  ListOrdered, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateRangePicker } from '../components/ui/date-range-picker';

const POLL_FAST_MS = 5000;
const POLL_SLOW_MS = 30000;

// IST-midnight helper — returns a "YYYY-MM-DDTHH:mm" string suitable for
// <input type="datetime-local"> in the browser's local timezone, picking
// the day boundary in Asia/Kolkata. This way "Today" + "Yesterday" presets
// always match the cron's idea of an IST calendar day.
function istDateInputValue(daysOffset = 0, endOfDay = false) {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const istShifted = new Date(Date.now() + IST_OFFSET_MS);
  istShifted.setUTCHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, 0);
  istShifted.setUTCDate(istShifted.getUTCDate() + daysOffset);
  // Render as the datetime-local input expects: YYYY-MM-DDTHH:mm (local).
  const real = new Date(istShifted.getTime() - IST_OFFSET_MS);
  const pad = (n) => String(n).padStart(2, '0');
  return `${real.getFullYear()}-${pad(real.getMonth() + 1)}-${pad(real.getDate())}T${pad(real.getHours())}:${pad(real.getMinutes())}`;
}

const STATUS_FILTERS = [
  { id: 'all',                label: 'All' },
  { id: 'pending_transcribe', label: 'Pending Transcribe' },
  { id: 'transcribing',       label: 'Transcribing' },
  { id: 'pending_translate',  label: 'Pending Translate' },
  { id: 'translating',        label: 'Translating' },
  { id: 'pending_analyze',    label: 'Pending Analyze' },
  { id: 'analyzing',          label: 'Analyzing' },
  { id: 'completed',          label: 'Completed' },
  { id: 'failed',             label: 'Failed' },
  { id: 'ineligible',         label: 'Skipped' },
];

const STAGE_META = {
  pending_transcribe: { label: 'pending transcribe', cls: 'bg-slate-500/15 text-slate-300', icon: Clock },
  transcribing:       { label: 'transcribing',       cls: 'bg-violet-500/15 text-violet-300', icon: Loader2, spin: true },
  pending_translate:  { label: 'pending translate',  cls: 'bg-sky-500/15 text-sky-300', icon: Clock },
  translating:        { label: 'translating',        cls: 'bg-indigo-500/15 text-indigo-300', icon: Loader2, spin: true },
  pending_analyze:    { label: 'pending analyze',    cls: 'bg-teal-500/15 text-teal-300', icon: Clock },
  analyzing:          { label: 'analyzing',          cls: 'bg-amber-500/15 text-amber-300', icon: Loader2, spin: true },
  completed:          { label: 'completed',          cls: 'bg-emerald-500/15 text-emerald-300', icon: CheckCircle2 },
  failed:             { label: 'failed',             cls: 'bg-rose-500/15 text-rose-300', icon: XCircle },
  ineligible:         { label: 'skipped',            cls: 'bg-slate-700/40 text-slate-400 border border-slate-700/60', icon: XCircle },
  unknown:            { label: 'unknown',            cls: 'bg-slate-500/15 text-slate-300', icon: Clock },
};

// Mirror the pattern from CallsListPage / ProcessedCallsPage: open a blank
// tab first (avoids popup blockers), copy the auth session into the new
// tab's sessionStorage so the user isn't bounced to /login, then navigate
// the new tab to the target URL.
function openCallInNewTab(callId) {
  const detailPath = `/calls/${encodeURIComponent(callId)}`;
  const newTab = window.open('', '_blank');
  if (!newTab) {
    // Popup blocked — fall back to same-tab nav.
    window.location.href = detailPath;
    return;
  }
  try {
    const token = sessionStorage.getItem('vcm_token');
    const userData = sessionStorage.getItem('vcm_user');
    if (token) newTab.sessionStorage.setItem('vcm_token', token);
    if (userData) newTab.sessionStorage.setItem('vcm_user', userData);
    newTab.location.href = detailPath;
  } catch (err) {
    // Cross-origin or storage quota issue — give up auth transfer; the
    // new tab will redirect through /login but at least it lands somewhere.
    newTab.location.href = detailPath;
  }
}

function fmtDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function fmtMs(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '-';
  const n = Number(ms);
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n}ms`;
}

function ScoreBadge({ score, source }) {
  if (score == null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const n = Number(score);
  const cls = n >= 90 ? 'score-excellent'
    : n >= 75 ? 'score-good'
    : n >= 60 ? 'score-review'
    : 'score-critical';
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium tabular-nums ${cls}`}>{n}</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{source}</span>
    </div>
  );
}

function StagePill({ stage, ineligibleReason }) {
  const meta = STAGE_META[stage] || STAGE_META.unknown;
  const Icon = meta.icon;
  const label = stage === 'ineligible' && ineligibleReason
    ? `skipped · ${ineligibleReason}`
    : meta.label;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${meta.cls}`} title={label}>
      <Icon className={`w-3 h-3 ${meta.spin ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

function StatCard({ label, value, color, active, onClick }) {
  const interactive = typeof onClick === 'function';
  const base = 'bg-card border rounded-xl p-4 transition-colors text-left w-full';
  const borderCls = active ? 'border-primary ring-1 ring-primary/40' : 'border-border';
  const hoverCls = interactive ? 'hover:border-primary/60 cursor-pointer' : '';
  return (
    <button type="button" onClick={onClick} disabled={!interactive} className={`${base} ${borderCls} ${hoverCls}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums mt-1 ${color || ''}`}>{value}</p>
    </button>
  );
}

// Composite card that groups a stage's two related buckets ("Queue" and
// "In Flight") under a single header. Each bucket is independently
// clickable so the table can be filtered to just that bucket. Each cell
// shows the *selected-range* count prominently and the *overall* count
// (across all dates) muted below — so the user can see local progress
// without losing sight of how much work exists project-wide.
function StageQueueCard({
  title,
  leftLabel = 'Queue',
  rightLabel = 'In Flight',
  queueValue, queueOverall, queueColor, queueStatus,
  inFlightValue, inFlightOverall, inFlightColor, inFlightStatus,
  activeStatus, onClick,
}) {
  const cellCls = (statusId) => {
    const isActive = activeStatus === statusId;
    return `rounded-lg p-2.5 text-left transition-colors border ${
      isActive
        ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
        : 'border-border bg-background/40 hover:border-primary/60'
    }`;
  };
  const renderOverall = (n) => (n != null
    ? <span className="text-[10px] text-muted-foreground">of {n} overall</span>
    : null);
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={cellCls(queueStatus)} onClick={() => onClick(queueStatus)}>
          <p className="text-[10px] text-muted-foreground">{leftLabel}</p>
          <p className={`text-xl font-semibold tabular-nums leading-tight ${queueColor || ''}`}>{queueValue}</p>
          {renderOverall(queueOverall)}
        </button>
        <button type="button" className={cellCls(inFlightStatus)} onClick={() => onClick(inFlightStatus)}>
          <p className="text-[10px] text-muted-foreground">{rightLabel}</p>
          <p className={`text-xl font-semibold tabular-nums leading-tight ${inFlightColor || ''}`}>{inFlightValue}</p>
          {renderOverall(inFlightOverall)}
        </button>
      </div>
    </div>
  );
}

// Compact progress display for one manual request — shows the headline
// counts (eligible / done / pending) plus a tiny stacked bar. The numbers
// are computed live server-side on every list refresh.
function ManualRequestProgress({ r }) {
  const c = r?.counts;
  if (!c) return <span className="text-xs text-muted-foreground">—</span>;
  const done = c.completed || 0;
  const inflight = c.processing || 0;
  const failed = c.failed || 0;
  const pending = c.pending || 0;
  const eligible = Math.max(1, c.eligible || (done + inflight + failed + pending));
  const pct = (n) => `${Math.min(100, Math.round((n / eligible) * 100))}%`;
  return (
    <div className="flex flex-col gap-1 min-w-[170px]">
      <div className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
        <span className="text-emerald-300">{done}</span> done ·{' '}
        {inflight > 0 && <><span className="text-amber-300">{inflight}</span> live · </>}
        {failed > 0 && <><span className="text-rose-300">{failed}</span> fail · </>}
        <span className="text-sky-300">{pending}</span> pending
        <span className="text-muted-foreground"> / {c.eligible} eligible</span>
        {c.ineligible > 0 && <span className="text-slate-500"> · {c.ineligible} skipped</span>}
      </div>
      <div className="h-1 w-full rounded overflow-hidden bg-background/60 flex">
        <div className="h-full bg-emerald-400/80" style={{ width: pct(done) }} />
        <div className="h-full bg-amber-400/80"   style={{ width: pct(inflight) }} />
        <div className="h-full bg-rose-400/80"    style={{ width: pct(failed) }} />
        <div className="h-full bg-sky-400/40"     style={{ width: pct(pending) }} />
      </div>
      {r.synced_at && (
        <div className="text-[10px] text-muted-foreground">
          synced {r.synced_count ?? 0} new at {new Date(r.synced_at).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, sortKey, currentSort, order, onSort }) {
  const active = currentSort === sortKey;
  const Icon = active ? (order === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className="text-left px-3 py-2 select-none">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide text-[11px] ${active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}

const EMPTY_FILTERS = {
  fromDate: '',
  toDate: '',
  agent_name: '',
  direction: '',
  scoreRange: '',  // '90-100' | '75-89' | '60-74' | 'lt60'
  detected_language: '',
  escalation: '',  // 'true' | 'false'
};

function formatLanguage(value) {
  const key = String(value || '').trim().toLowerCase();
  const labels = { te: 'Telugu', ta: 'Tamil', hi: 'Hindi', ml: 'Malayalam', kn: 'Kannada', other: 'Other' };
  return labels[key] || 'Other';
}

// Default the date range to "today IST" so the page on load matches what
// the startup-sweep cron just processed. User can flip to Yesterday or
// pick a custom range from the calendar.
function defaultFilters() {
  return {
    ...EMPTY_FILTERS,
    fromDate: istDateInputValue(0, false),
    toDate:   istDateInputValue(0, true),
  };
}

// Presets the user can one-click in the date row. Each computes both ends.
const DATE_PRESETS = [
  { id: 'today',     label: 'Today (IST)',     compute: () => ({ fromDate: istDateInputValue(0, false),  toDate: istDateInputValue(0, true)  }) },
  { id: 'yesterday', label: 'Yesterday (IST)', compute: () => ({ fromDate: istDateInputValue(-1, false), toDate: istDateInputValue(-1, true) }) },
  { id: 'last7',     label: 'Last 7 days',     compute: () => ({ fromDate: istDateInputValue(-6, false), toDate: istDateInputValue(0, true)  }) },
];

export default function GeminiProcessingPage() {
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [filtersDraft, setFiltersDraft] = useState(defaultFilters);
  const [sort, setSort] = useState('date_time');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const pollTimer = useRef(null);
  const limit = 50;

  // Populate the Language dropdown from distinct detected_language values
  // in the calls table. Same source the Processed Calls page uses.
  useEffect(() => {
    api.getCallLanguages()
      .then((res) => setAvailableLanguages(res?.languages || []))
      .catch(() => { /* silent */ });
  }, []);

  function scoreRangeToMinMax(range) {
    switch (range) {
      case '90-100': return { min_score: 90, max_score: 100 };
      case '75-89':  return { min_score: 75, max_score: 89  };
      case '60-74':  return { min_score: 60, max_score: 74  };
      case 'lt60':   return { min_score: 0,  max_score: 59  };
      default:       return {};
    }
  }

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const scoreParams = scoreRangeToMinMax(filters.scoreRange);
      const callsParams = {
        // Always use the explicit date range — defaults to today IST on
        // first load, user can change via the date pickers or presets.
        fromDate: filters.fromDate || undefined,
        toDate:   filters.toDate || undefined,
        status,
        search: searchDebounced,
        agent_name: filters.agent_name || undefined,
        direction:  filters.direction || undefined,
        detected_language:    filters.detected_language || undefined,
        escalation_detected:  filters.escalation || undefined,
        ...scoreParams,
        sort,
        order,
        page,
        limit,
      };
      const summaryParams = {
        fromDate: filters.fromDate || undefined,
        toDate:   filters.toDate || undefined,
      };

      const [s, c] = await Promise.all([
        api.getGeminiProcessingSummary(summaryParams),
        api.getGeminiProcessingCalls(callsParams),
      ]);
      setSummary(s);
      setCalls(c.rows || []);
      setTotal(c.total || 0);
      setPages(c.pages || 1);
    } catch (err) {
      if (!silent) toast.error(err?.message || 'Failed to load Gemini processing');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [status, searchDebounced, page, filters, sort, order]);

  useEffect(() => { loadData(); }, [loadData]);

  // Polling — fast when something is in flight, slow when steady.
  useEffect(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    const isBusy = fetching
      || summary?.pipeline?.running
      || Number(summary?.processing || 0) > 0;
    const interval = isBusy ? POLL_FAST_MS : POLL_SLOW_MS;
    pollTimer.current = setTimeout(() => {
      loadData({ silent: true });
    }, interval);
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [summary, fetching, loadData]);

  const handleFetchRecords = async () => {
    if (!filters.fromDate || !filters.toDate) {
      toast.error('Pick a from and to date first');
      return;
    }
    setFetching(true);
    try {
      // POST runs the SmartFlo sync server-side and returns live counts.
      const result = await api.createGeminiManualRequest({
        fromDate: filters.fromDate,
        toDate:   filters.toDate,
      });
      const c = result?.counts || {};
      const synced = result?.synced_count ?? 0;
      toast.success(
        `Queued · synced ${synced} new · ${c.completed ?? 0} already processed · ${c.pending ?? 0} pending`
      );
      setDrawerOpen(true);
      setTimeout(() => loadData({ silent: true }), 800);
      setTimeout(() => loadManualRequests(), 200);
    } catch (err) {
      // 409 = duplicate range already in the queue. Don't open the drawer
      // automatically — just alert the user so they can decide whether to
      // wait or cancel the existing one.
      if (err?.status === 409) {
        toast.warning(err.message || 'A request for this date range is already queued');
      } else {
        toast.error(err?.message || 'Failed to queue request');
      }
    } finally {
      setFetching(false);
    }
  };

  // Manual request queue state
  const [manualRequests, setManualRequests] = useState([]);
  // Synthetic auto-queue entries (today + yesterday) returned alongside the
  // manual requests so the operator can see the full priority chain in one
  // place. These are read-only — the scheduler picks them up first.
  const [autoQueue, setAutoQueue] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dragIdRef = useRef(null);

  const loadManualRequests = useCallback(async () => {
    try {
      const data = await api.getGeminiManualRequests();
      setManualRequests(data?.rows || []);
      setAutoQueue(data?.autoQueue || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadManualRequests(); }, [loadManualRequests]);
  // Refresh manual list along with the regular polling tick.
  useEffect(() => {
    if (!summary) return;
    loadManualRequests();
  }, [summary, loadManualRequests]);

  const queuedRequests = manualRequests.filter((r) => r.status === 'queued');
  const otherRequests = manualRequests.filter((r) => r.status !== 'queued');

  const handleDragStart = (id) => (e) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (targetId) => async (e) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const ids = queuedRequests.map((r) => r.id);
    const srcIdx = ids.indexOf(sourceId);
    const dstIdx = ids.indexOf(targetId);
    if (srcIdx < 0 || dstIdx < 0) return;
    ids.splice(dstIdx, 0, ids.splice(srcIdx, 1)[0]);

    // Optimistic reorder
    const reordered = [
      ...ids.map((id) => queuedRequests.find((r) => r.id === id)),
      ...otherRequests,
    ];
    setManualRequests(reordered);
    setReordering(true);
    try {
      await api.reorderGeminiManualRequests(ids);
      loadManualRequests();
    } catch (err) {
      toast.error(err?.message || 'Failed to reorder');
      loadManualRequests();
    } finally {
      setReordering(false);
    }
  };

  const handleCancelRequest = async (id) => {
    try {
      await api.cancelGeminiManualRequest(id);
      loadManualRequests();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel');
    }
  };

  const pipelineRunning = Boolean(summary?.pipeline?.running) || fetching;
  const pollIsFast = useMemo(() => (
    fetching || pipelineRunning || Number(summary?.processing || 0) > 0
  ), [fetching, pipelineRunning, summary]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Cpu className="w-5 h-5 text-primary" />
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-lg font-semibold">Auto Processing</h1>
          <p className="text-xs text-muted-foreground">
            {filters.fromDate?.slice(0, 10)} → {filters.toDate?.slice(0, 10)}
            {' · '}total {summary?.total ?? '-'} · auto-refresh {pollIsFast ? 'every 5s' : 'every 30s'}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => loadData()}
          disabled={refreshing}
          data-testid="gemini-page-refresh"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { loadManualRequests(); setDrawerOpen(true); }}
          data-testid="gemini-page-show-requests"
        >
          <ListOrdered className="w-4 h-4 mr-1" />
          Show Requests List
          {manualRequests.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary/20 text-primary text-[10px] font-medium">
              {manualRequests.length}
            </span>
          )}
        </Button>
        <Button
          size="sm"
          onClick={handleFetchRecords}
          disabled={fetching}
          data-testid="gemini-page-fetch-records"
        >
          {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Cpu className="w-4 h-4 mr-1" />}
          Queue Selected Range
        </Button>
      </div>

      {/* Date controls — sit directly under the heading. Presets and the
          calendar range apply immediately (no Apply button needed). */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {DATE_PRESETS.map(p => {
            const isActive = filters.fromDate === p.compute().fromDate && filters.toDate === p.compute().toDate;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  const next = p.compute();
                  setFiltersDraft(d => ({ ...d, ...next }));
                  setFilters(f => ({ ...f, ...next }));
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <DateRangePicker
          value={{
            from_date: filters.fromDate ? filters.fromDate.slice(0, 10) : '',
            to_date:   filters.toDate   ? filters.toDate.slice(0, 10)   : '',
          }}
          showValue={Boolean(filters.fromDate || filters.toDate)}
          placeholder="Date range"
          onChange={(range) => {
            const next = {
              fromDate: range.from_date ? `${range.from_date}T00:00` : '',
              toDate:   range.to_date   ? `${range.to_date}T23:59`   : '',
            };
            setFiltersDraft(d => ({ ...d, ...next }));
            setFilters(f => ({ ...f, ...next }));
            setPage(1);
          }}
          onClear={() => {
            setFiltersDraft(d => ({ ...d, fromDate: '', toDate: '' }));
            setFilters(f => ({ ...f, fromDate: '', toDate: '' }));
            setPage(1);
          }}
          buttonClassName="h-8 text-xs min-w-[220px]"
        />
      </div>

      {/* Pipeline-running banner — visible whenever the cron / manual run
          is actively walking through pending calls. The "In Flight" count
          can be 0 between calls, so this banner is the authoritative
          signal that work is happening right now. */}
      {pipelineRunning && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          <div className="flex-1 text-xs text-amber-200">
            Gemini pipeline is running
            {summary?.pipeline?.lastStartedAt && ` · started ${fmtDateTime(summary.pipeline.lastStartedAt)}`}
            {Number(summary?.processing || 0) > 0 && ` · ${summary.processing} call(s) currently in flight`}
          </div>
        </div>
      )}
      {!pipelineRunning && summary?.pipeline?.lastFinishedAt && (
        <div className="bg-card border border-border rounded-xl p-3 text-xs text-muted-foreground">
          Pipeline idle. Last run finished {fmtDateTime(summary.pipeline.lastFinishedAt)}.
          {summary.pipeline.lastResult && ` Last batch: ${summary.pipeline.lastResult.completed ?? 0} completed, ${summary.pipeline.lastResult.failed ?? 0} failed.`}
        </div>
      )}

      {/* Stage counters — grouped per stage as Queue + In Flight, plus the
          terminal Completed / Failed states. Total and Skipped are kept
          one line up in the header subtext so the main row stays focused. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <StageQueueCard
          title="Transcribe"
          queueValue={summary?.pending_transcribe ?? '-'}
          queueOverall={summary?.overall?.pending_transcribe}
          queueColor="text-slate-300"
          queueStatus="pending_transcribe"
          inFlightValue={summary?.transcribing ?? '-'}
          inFlightOverall={summary?.overall?.transcribing}
          inFlightColor="text-violet-300"
          inFlightStatus="transcribing"
          activeStatus={status}
          onClick={(s) => { setStatus(s); setPage(1); }}
        />
        <StageQueueCard
          title="Translate"
          queueValue={summary?.pending_translate ?? '-'}
          queueOverall={summary?.overall?.pending_translate}
          queueColor="text-sky-300"
          queueStatus="pending_translate"
          inFlightValue={summary?.translating ?? '-'}
          inFlightOverall={summary?.overall?.translating}
          inFlightColor="text-indigo-300"
          inFlightStatus="translating"
          activeStatus={status}
          onClick={(s) => { setStatus(s); setPage(1); }}
        />
        <StageQueueCard
          title="AI Analysis"
          queueValue={summary?.pending_analyze ?? '-'}
          queueOverall={summary?.overall?.pending_analyze}
          queueColor="text-teal-300"
          queueStatus="pending_analyze"
          inFlightValue={summary?.analyzing ?? '-'}
          inFlightOverall={summary?.overall?.analyzing}
          inFlightColor="text-amber-300"
          inFlightStatus="analyzing"
          activeStatus={status}
          onClick={(s) => { setStatus(s); setPage(1); }}
        />
        <StageQueueCard
          title="Completed"
          leftLabel="Processed"
          rightLabel="Skipped"
          queueValue={summary?.completed ?? '-'}
          queueColor="text-emerald-300"
          queueStatus="completed"
          inFlightValue={summary?.ineligible ?? '-'}
          inFlightColor="text-slate-400"
          inFlightStatus="ineligible"
          activeStatus={status}
          onClick={(s) => { setStatus(s); setPage(1); }}
        />
        <StatCard
          label="Failed"
          value={summary?.failed ?? '-'}
          color="text-rose-300"
          active={status === 'failed'}
          onClick={() => { setStatus('failed'); setPage(1); }}
        />
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 space-y-3">
        {/* Row 1: status pills + search + count */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => { setStatus(f.id); setPage(1); }}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  status === f.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="search call_id / agent / number"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 h-8 text-xs"
            />
          </div>
          <div className="text-xs text-muted-foreground">{total} call{total === 1 ? '' : 's'}</div>
        </div>

        {/* Row 2: agent + direction + language + escalation + score range + apply/clear */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="agent name"
            value={filtersDraft.agent_name}
            onChange={(e) => setFiltersDraft(d => ({ ...d, agent_name: e.target.value }))}
            className="h-8 text-xs w-36"
          />
          <select
            value={filtersDraft.direction}
            onChange={(e) => setFiltersDraft(d => ({ ...d, direction: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any direction</option>
            <option value="inbound">inbound</option>
            <option value="outbound">outbound</option>
          </select>
          <select
            value={filtersDraft.detected_language}
            onChange={(e) => setFiltersDraft(d => ({ ...d, detected_language: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any language</option>
            {availableLanguages.map((lang) => (
              <option key={lang} value={lang}>{formatLanguage(lang)}</option>
            ))}
          </select>
          <select
            value={filtersDraft.escalation}
            onChange={(e) => setFiltersDraft(d => ({ ...d, escalation: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any escalation</option>
            <option value="true">escalation detected</option>
            <option value="false">no escalation</option>
          </select>
          <select
            value={filtersDraft.scoreRange}
            onChange={(e) => setFiltersDraft(d => ({ ...d, scoreRange: e.target.value }))}
            className="h-8 text-xs rounded border border-border bg-background px-2"
          >
            <option value="">any score</option>
            <option value="90-100">excellent (90-100)</option>
            <option value="75-89">good (75-89)</option>
            <option value="60-74">needs review (60-74)</option>
            <option value="lt60">critical (&lt;60)</option>
          </select>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => { setFilters(filtersDraft); setPage(1); }}
          >
            Apply
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const cleared = { agent_name: '', direction: '', detected_language: '', escalation: '', scoreRange: '' };
              setFiltersDraft(d => ({ ...d, ...cleared }));
              setFilters(f => ({ ...f, ...cleared }));
              setPage(1);
            }}
          >
            <FilterX className="w-3 h-3 mr-1" />Clear
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Call</th>
                <SortHeader label="Agent"        sortKey="agent_name"   currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); setPage(1); }} />
                <SortHeader label="Date/Time"    sortKey="date_time"    currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <SortHeader label="Answered / Total" sortKey="answered" currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <SortHeader label="Status"       sortKey="status"       currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); setPage(1); }} />
                <SortHeader label="Score"        sortKey="score"        currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <th className="text-left px-3 py-2">Purpose</th>
                <th className="text-left px-3 py-2">Outcome</th>
                <SortHeader label="Translate ms" sortKey="translate_ms" currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <SortHeader label="Analysis ms"  sortKey="analysis_ms"  currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <SortHeader label="Processed"    sortKey="processed_at" currentSort={sort} order={order} onSort={(k) => { setSort(k); setOrder(o => sort === k ? (o === 'asc' ? 'desc' : 'asc') : 'desc'); setPage(1); }} />
                <th className="text-right px-3 py-2">Open</th>
              </tr>
            </thead>
            <tbody>
              {calls.length === 0 && (
                <tr><td colSpan={12} className="text-center text-xs text-muted-foreground p-6">No calls in this filter</td></tr>
              )}
              {calls.map(c => {
                // Score source preference per spec: default to Gemini; if
                // Claude analysis is available, show Claude instead.
                const useClaude = c.has_claude_analysis;
                const score = useClaude ? c.claude_score : c.gemini_score;
                const source = useClaude ? 'Claude' : (c.has_gemini_analysis ? 'Gemini' : null);
                const rawPurpose = useClaude ? c.claude_purpose : c.gemini_purpose;
                const leadsCount = useClaude ? c.claude_leads_count : c.gemini_leads_count;
                const purpose = rawPurpose === 'lead'
                  ? ((leadsCount || 0) > 0 ? 'lead' : 'other')
                  : rawPurpose;
                const outcome = useClaude ? c.claude_outcome : c.gemini_outcome;
                return (
                  <tr key={c.call_id} className="border-t border-border hover:bg-white/[0.02]">
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{c.call_id}</td>
                    <td className="px-3 py-2">{c.agent_name || '-'}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{c.date} {c.time}</td>
                    <td
                      className="px-3 py-2 text-xs tabular-nums whitespace-nowrap"
                      title={`call_duration=${c.call_duration}s · answered=${c.answered_seconds ?? 0}s`}
                    >
                      <span>{c.answered_seconds ?? 0}s</span>
                      <span className="text-muted-foreground"> / {c.call_duration}s</span>
                    </td>
                    <td className="px-3 py-2"><StagePill stage={c.stage} ineligibleReason={c.ineligible_reason} /></td>
                    <td className="px-3 py-2">{source ? <ScoreBadge score={score} source={source} /> : <span className="text-xs text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs">{purpose ? purpose.replace(/_/g, ' ') : '-'}</td>
                    <td className="px-3 py-2 text-xs">{outcome ? outcome.replace(/_/g, ' ') : '-'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtMs(c.gemini_translate_ms)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtMs(c.gemini_analysis_ms)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(c.gemini_processed_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => openCallInNewTab(c.call_id)}
                        className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2">
            <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex items-center gap-1">
              <Button variant="secondary" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Manual requests drawer — slides in from the right at 50% width
          when the "Show Requests List" button is clicked. */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer */}
          <aside
            className="fixed top-0 right-0 z-50 h-screen w-full sm:w-1/2 bg-card border-l border-border shadow-2xl flex flex-col"
            role="dialog"
            aria-label="Manual Requests"
          >
            <div className="px-4 py-3 border-b border-border flex items-center gap-3 shrink-0">
              <ListOrdered className="w-4 h-4 text-primary" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold">Processing Queue</h3>
                <p className="text-[11px] text-muted-foreground truncate">
                  Auto: today &rarr; yesterday &rarr; manual · {queuedRequests.length} manual queued · drag a manual row to reorder
                </p>
              </div>
              {reordering && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0">
                  <tr>
                    <th className="w-8"></th>
                    <th className="text-left px-3 py-2">From</th>
                    <th className="text-left px-3 py-2">To</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Progress</th>
                    <th className="text-left px-3 py-2">Requested At</th>
                    <th className="text-left px-3 py-2">Result / Error</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {autoQueue.map((r) => {
                    const c = r.counts || {};
                    const statusCls = r.status === 'processing' ? 'bg-amber-500/15 text-amber-300'
                      : r.status === 'queued' ? 'bg-sky-500/15 text-sky-300'
                      : 'bg-emerald-500/15 text-emerald-300';
                    const StatusIcon = r.status === 'processing' ? Loader2
                      : r.status === 'queued' ? Clock
                      : CheckCircle2;
                    return (
                      <tr key={r.id} className="border-t border-border bg-white/[0.015]">
                        <td className="px-2 py-2 text-[10px] uppercase tracking-wide text-violet-300 font-semibold">Auto</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap" colSpan={2}>{r.label}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusCls}`}>
                            <StatusIcon className={`w-3 h-3 ${r.status === 'processing' ? 'animate-spin' : ''}`} />{r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2"><ManualRequestProgress r={r} /></td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">system</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {c ? `${c.completed ?? 0} done · ${c.pending ?? 0} pending` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">—</td>
                      </tr>
                    );
                  })}
                  {manualRequests.length === 0 && autoQueue.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-xs text-muted-foreground p-6">No manual requests yet — close this and use "Queue Selected Range" on the main page.</td></tr>
                  )}
                  {queuedRequests.map((r) => (
                    <tr
                      key={r.id}
                      draggable
                      onDragStart={handleDragStart(r.id)}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop(r.id)}
                      className="border-t border-border hover:bg-white/[0.02] cursor-move"
                    >
                      <td className="px-2 py-2 text-muted-foreground"><GripVertical className="w-4 h-4" /></td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.from_date).toLocaleDateString()}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.to_date).toLocaleDateString()}</td>
                      <td className="px-3 py-2"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-sky-500/15 text-sky-300"><Clock className="w-3 h-3" />queued</span></td>
                      <td className="px-3 py-2"><ManualRequestProgress r={r} /></td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.requested_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">—</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleCancelRequest(r.id)}
                          className="text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 text-xs"
                        >
                          <Trash2 className="w-3 h-3" />Cancel
                        </button>
                      </td>
                    </tr>
                  ))}
                  {otherRequests.map((r) => {
                    const cls = r.status === 'processing' ? 'bg-amber-500/15 text-amber-300'
                      : r.status === 'completed' ? 'bg-emerald-500/15 text-emerald-300'
                      : r.status === 'failed' ? 'bg-rose-500/15 text-rose-300'
                      : 'bg-slate-500/15 text-slate-300';
                    const Icon = r.status === 'processing' ? Loader2
                      : r.status === 'completed' ? CheckCircle2
                      : r.status === 'failed' ? XCircle
                      : Clock;
                    const summary = r.status === 'completed' && r.result
                      ? `transcribe ${r.result.transcribe?.ok ?? '?'} · translate ${r.result.translate?.ok ?? '?'} · analyze ${r.result.analyze?.ok ?? '?'}`
                      : r.status === 'failed' && r.error
                      ? String(r.error).slice(0, 120)
                      : '—';
                    return (
                      <tr key={r.id} className="border-t border-border">
                        <td className="px-2 py-2"></td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.from_date).toLocaleDateString()}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.to_date).toLocaleDateString()}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${cls}`}>
                            <Icon className={`w-3 h-3 ${r.status === 'processing' ? 'animate-spin' : ''}`} />
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2"><ManualRequestProgress r={r} /></td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.requested_at).toLocaleString()}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground" title={summary}>{summary}</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
